import type { LucideIcon } from 'lucide-react';

interface Tab {
  id: string;
  label: string;
  count?: number;
  icon?: LucideIcon;
}

interface TabNavProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: 'pill' | 'underline';
  className?: string;
}

export function TabNav({
  tabs,
  activeTab,
  onTabChange,
  variant = 'pill',
  className = '',
}: TabNavProps) {
  if (variant === 'underline') {
    return (
      <div className={`flex gap-6 border-b border-border ${className}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`pb-3 px-1 text-sm font-medium transition-colors relative flex items-center gap-2 ${
              activeTab === tab.id
                ? 'text-[#E84A2B]'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.icon && <tab.icon className="w-4 h-4" />}
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === tab.id
                    ? 'bg-[#E84A2B]/10 text-[#E84A2B]'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {tab.count}
              </span>
            )}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E84A2B]" />
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 bg-card border border-border rounded-lg p-1.5 w-fit ${className}`}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            activeTab === tab.id
              ? 'bg-orange-600 text-white shadow-md'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          {tab.icon && <tab.icon className="w-4 h-4" />}
          {tab.label}
          {tab.count !== undefined && (
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
              }`}
            >
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export type { Tab, TabNavProps };
